'use client';

import {
  CameraIcon,
  EnvelopeIcon,
  MusicalNoteIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/Button';
import { cn } from '../../../lib/utils';

export type ShareChannelHandlers = {
  onShareEmail: () => void;
  onShareFacebook: () => void;
  onShareInstagram: () => void;
  onShareTiktok: () => void;
};

/** Figma mobile share shell: dark panel, pink icon tiles, white label. */
export type ShareOverlayPalette = 'default' | 'figmaDark';

export type ShareToSocialsPanelProps = ShareChannelHandlers & {
  /** `row` = icon strip (mobile Top 3 footer); `column` = labeled list (desktop sidebar / list slide). */
  layout?: 'row' | 'column';
  /** When true, Email is disabled (e.g. no persisted `matchResultsId`). */
  emailDisabled?: boolean;
  /** Dark overlay styling used inside `MatchShareResultsFlow`. */
  palette?: ShareOverlayPalette;
  className?: string;
};

function FacebookGlyph({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-600 font-bold text-primary-foreground',
        className,
      )}
      aria-hidden
    >
      f
    </span>
  );
}

export function ShareToSocialsPanel({
  layout = 'column',
  onShareEmail,
  onShareFacebook,
  onShareInstagram,
  onShareTiktok,
  emailDisabled = false,
  palette = 'default',
  className,
}: ShareToSocialsPanelProps) {
  const dark = palette === 'figmaDark';

  const itemClassDefault =
    layout === 'row'
      ? 'flex flex-1 flex-col items-center gap-2 rounded-xl border border-border bg-card p-3 text-center shadow-sm'
      : 'flex w-full items-center justify-center gap-3 rounded-xl border border-border bg-card px-3 py-3 text-center shadow-sm transition-colors hover:bg-accent/60';

  /** Figma mobile: full pink touch tiles, no nested card chrome. */
  const itemClassDarkRow =
    'flex size-12 min-h-12 min-w-12 flex-1 items-center justify-center rounded-xl border-0 bg-primary-600 p-0 text-primary-foreground shadow-none hover:bg-primary-500 sm:size-14 sm:min-h-14 sm:min-w-14';

  const itemClassDarkCol =
    'flex w-full items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-center text-white shadow-none transition-colors hover:bg-white/10';

  const itemClass =
    dark && layout === 'row'
      ? itemClassDarkRow
      : dark && layout === 'column'
        ? itemClassDarkCol
        : itemClassDefault;

  const iconWrapDefault =
    'flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-primary-foreground';

  return (
    <div
      className={cn(
        'rounded-2xl border p-4 shadow-sm',
        dark
          ? 'border-white/10 bg-[#352a47]/90 backdrop-blur-sm'
          : 'border-border bg-card/80',
        layout === 'row' && 'flex flex-col gap-3',
        layout === 'column' && 'flex flex-col gap-2',
        className,
      )}
    >
      <p
        className={cn(
          'mb-1 w-full text-sm font-semibold',
          dark ? 'text-white/95' : 'text-foreground',
        )}
      >
        Share to socials
      </p>
      <div
        className={cn(
          layout === 'row' &&
            cn(
              'flex w-full gap-2 sm:gap-3',
              dark ? 'justify-center' : 'flex-wrap justify-center sm:justify-between',
            ),
          layout === 'column' && 'flex flex-col gap-2',
        )}
      >
        <Button
          type='button'
          variant={
            dark && layout === 'row'
              ? 'primary'
              : dark && layout === 'column'
                ? 'ghost'
                : 'secondary'
          }
          className={cn(
            itemClass,
            !(dark && layout === 'row') && 'h-auto min-h-0 shadow-sm',
          )}
          aria-label='Share via email'
          aria-disabled={emailDisabled || undefined}
          disabled={emailDisabled}
          title={
            emailDisabled
              ? 'Run Find matches with share enabled to email results'
              : undefined
          }
          onClick={onShareEmail}
        >
          {dark && layout === 'row' ? (
            <EnvelopeIcon className='size-6 sm:size-7' aria-hidden />
          ) : (
            <>
              <span className={cn(iconWrapDefault)}>
                <EnvelopeIcon className='size-5' aria-hidden />
              </span>
              {layout === 'column' ? (
                <span
                  className={cn(
                    'font-medium',
                    dark ? 'text-white' : 'text-foreground',
                  )}
                >
                  Email
                </span>
              ) : null}
            </>
          )}
        </Button>
        <Button
          type='button'
          variant={
            dark && layout === 'row'
              ? 'primary'
              : dark && layout === 'column'
                ? 'ghost'
                : 'secondary'
          }
          className={cn(
            itemClass,
            !(dark && layout === 'row') && 'h-auto min-h-0 shadow-sm',
          )}
          aria-label='Share via Facebook'
          onClick={onShareFacebook}
        >
          {dark && layout === 'row' ? (
            <span className='text-lg font-bold' aria-hidden>
              f
            </span>
          ) : (
            <>
              <FacebookGlyph />
              {layout === 'column' ? (
                <span
                  className={cn(
                    'font-medium',
                    dark ? 'text-white' : 'text-foreground',
                  )}
                >
                  Facebook
                </span>
              ) : null}
            </>
          )}
        </Button>
        <Button
          type='button'
          variant={
            dark && layout === 'row'
              ? 'primary'
              : dark && layout === 'column'
                ? 'ghost'
                : 'secondary'
          }
          className={cn(
            itemClass,
            !(dark && layout === 'row') && 'h-auto min-h-0 shadow-sm',
          )}
          aria-label='Share via Instagram'
          onClick={onShareInstagram}
        >
          {dark && layout === 'row' ? (
            <CameraIcon className='size-6 sm:size-7' aria-hidden />
          ) : (
            <>
              <span className={cn(iconWrapDefault)}>
                <CameraIcon className='size-5' aria-hidden />
              </span>
              {layout === 'column' ? (
                <span
                  className={cn(
                    'font-medium',
                    dark ? 'text-white' : 'text-foreground',
                  )}
                >
                  Instagram
                </span>
              ) : null}
            </>
          )}
        </Button>
        <Button
          type='button'
          variant={
            dark && layout === 'row'
              ? 'primary'
              : dark && layout === 'column'
                ? 'ghost'
                : 'secondary'
          }
          className={cn(
            itemClass,
            !(dark && layout === 'row') && 'h-auto min-h-0 shadow-sm',
          )}
          aria-label='Share via TikTok'
          onClick={onShareTiktok}
        >
          {dark && layout === 'row' ? (
            <MusicalNoteIcon className='size-6 sm:size-7' aria-hidden />
          ) : (
            <>
              <span className={cn(iconWrapDefault)}>
                <MusicalNoteIcon className='size-5' aria-hidden />
              </span>
              {layout === 'column' ? (
                <span
                  className={cn(
                    'font-medium',
                    dark ? 'text-white' : 'text-foreground',
                  )}
                >
                  TikTok
                </span>
              ) : null}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
